import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { formatMonthShortDay } from '@plato/ui-legacy/lib/date-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { Join } from '@plato/ui/containers/join';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatOrderId } from '../../../lib/order-helpers.ts';
import type { CrmTaskInputDTO } from '../../../server/dto/crm-task-input-dto.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import { api } from '../../../utils/api.ts';
import { useDetailQuoteOrder } from '../../quote-order-detail/hooks/use-detail-quote-order.tsx';
import type { TaskFormSuggestion } from '../../tasks/components/task-form-dialog.tsx';
import type { TaskListItem } from '../log-interaction-tasks-context.ts';
import { useAddStagedTasks, useStagedTasks } from '../log-interaction-tasks-provider.tsx';
import type { LogInteractionOfferPosition } from './log-interaction-offer-card.tsx';
import { LogInteractionOfferCard } from './log-interaction-offer-card.tsx';
import { LogInteractionOfferSection } from './log-interaction-offer-section.tsx';
import { LogInteractionTaskCard, TaskDialog } from './log-interaction-task-card.tsx';

type LogInteractionOfferListProps = {
    customerId: string;
    customerName?: string;
};

// Every open quote is listed (the design dropped the 3-item cap); the ceiling is
// only there to keep a pathological customer from flooding the dialog.
const openQuotesLimit = 5;
const draftsLimit = 5;

// `CrmTaskInputDTO` types a link's `modelType` as a plain string, so this is a string
// comparison; taking the value off `SearchEntity` — what writes these links — is what
// keeps it honest, since a wrong member stops compiling.
const salesQuoteLinkType: string = SearchEntity.SalesQuote;

// A task claims an offer through its ERP link id, which is the quote's `sales_order_id`.
function useTasksLinkedToQuote(salesOrderId: string): TaskListItem[] {
    return useStagedTasks().filter((item) =>
        item.values.links.some(
            (link) => link.modelType === salesQuoteLinkType && link.modelId === salesOrderId,
        ),
    );
}

type QuoteOfferCardProps = {
    salesOrderId: string;
    /** The quote's ERP identifier — digits, letters or both. */
    displayId: string;
    customerId: string;
    customerName?: string;
    amount: number;
    positionsCount: number;
    margin?: number;
    creator?: string;
    kommission?: string;
};

// One card per sent quote. The positions live on the quote detail, which is only
// fetched while the card is expanded.
function QuoteOfferCard({
    salesOrderId,
    displayId,
    customerId,
    customerName,
    amount,
    positionsCount,
    margin,
    creator,
    kommission,
}: QuoteOfferCardProps): ReactNode {
    const { t } = useTranslation('notes');
    const [isExpanded, setIsExpanded] = useState(false);
    const tasks = useTasksLinkedToQuote(salesOrderId);
    const addTasks = useAddStagedTasks();

    const title = t('notes__offers_quote_title', { displayId });

    const openCreateTask = (): void => {
        const initialValues: Partial<CrmTaskInputDTO> = {
            taskIntent: 'GENERIC',
            links: [
                {
                    modelType: SearchEntity.Customer,
                    modelId: customerId,
                    title: customerName ?? null,
                },
                {
                    modelType: SearchEntity.SalesQuote,
                    modelId: salesOrderId,
                    title: displayId,
                },
            ],
        };

        // Entity-first, so a task reads "<offer> als erledigt markieren", not verb-first.
        const suggestions: TaskFormSuggestion[] = [
            {
                label: t('notes__offers_confirm_suggestion'),
                description: t('notes__offers_confirm_task', { offer: title }),
                taskIntent: 'QUOTE_CONFIRM',
            },
            {
                label: t('notes__offers_lost_suggestion'),
                description: t('notes__offers_lost_task', { offer: title }),
                taskIntent: 'QUOTE_MARK_LOST',
            },
        ];

        void DialogStateManager.show(TaskDialog, {
            title: t('tasks__form_create_title', { ns: 'tasks' }),
            initialValues,
            suggestions,
            onSubmit: (task) => {
                addTasks([task]);
            },
        });
    };

    const { data, isPending } = useDetailQuoteOrder({
        transactionId: salesOrderId,
        enabled: isExpanded,
    });

    const positions: LogInteractionOfferPosition[] = (data?.lineItems ?? []).map((lineItem) => ({
        id: lineItem.sales_order_item_id,
        // `product_name` already falls back from the line item to the product dim
        // server-side; the id keeps a nameless position visible rather than blank.
        description: lineItem.product_name ?? lineItem.product_variant_id ?? lineItem.sales_order_item_id,
        quantity: lineItem.quantity,
        total: lineItem.line_total ?? 0,
    }));

    // Joined so the quote and the tasks hanging off it read as one block. The Join stays
    // even with no tasks — swapping the root element would remount an expanded card shut.
    return (
        <Join orientation="vertical">
            <LogInteractionOfferCard
                title={title}
                stateLabel={t('OPEN', { ns: 'order-status' })}
                stateTone="warning"
                amount={amount}
                expansion={{
                    positionsCount,
                    positions,
                    margin,
                    creator,
                    kommission,
                    isPending: isExpanded && isPending,
                }}
                onExpandedChange={setIsExpanded}
                onCreateTask={openCreateTask}
            />
            {tasks.map((task) => (
                <LogInteractionTaskCard key={task.key} item={task} />
            ))}
        </Join>
    );
}

// The customer's open quotes plus their unsent quote drafts, acted on while logging
// the interaction. Same procedures as the quotes list and the drafts list.
export function LogInteractionOfferList({
    customerId,
    customerName,
}: LogInteractionOfferListProps): ReactNode {
    const { t } = useTranslation('notes');
    const locale = useResolvedLocale();
    const appConfig = useAppConfig();

    const quotesQuery = api.platoUser.getManyQuotesOrders.getWithFilters.useQuery(
        {
            erpCustomerIds: [customerId],
            orderTypes: [SrSalesOrderType.QUOTE],
            orderStatus: ['OPEN'],
            limit: openQuotesLimit,
        },
        { enabled: !!customerId },
    );

    const draftsQuery = api.platoUser.query.salesDraft.getSalesDraftList.getWithFilters.useQuery(
        {
            customers: [customerId],
            users: [],
            states: ['NEW'],
            types: ['QUOTE'],
            limit: draftsLimit,
        },
        { enabled: !!customerId },
    );

    const quotes = quotesQuery.data?.result ?? [];
    const drafts = draftsQuery.data?.items ?? [];

    const isEmpty = quotes.length === 0 && drafts.length === 0;
    const isPending = quotesQuery.isPending || draftsQuery.isPending;
    // Sent quotes only, so a draft's total doesn't inflate the summary.
    const totalAmountNet = quotes.reduce((total, quote) => total + (quote.total_amount_net ?? 0), 0);

    return (
        <LogInteractionOfferSection
            quoteCount={quotes.length}
            quoteTotal={totalAmountNet}
            state={isEmpty ? (isPending ? 'pending' : 'empty') : 'ready'}
        >
            {/* Drafts first — an unsent quote is the one still waiting on the rep. */}
            {drafts.map((draft) => (
                <LogInteractionOfferCard
                    key={draft.draft_order_id}
                    draft
                    // A draft has no ERP number; the drafts list dates them too.
                    title={
                        draft.created_at
                            ? formatMonthShortDay(new Date(draft.created_at), locale)
                            : t('notes__offers_draft_title')
                    }
                    stateLabel={t('notes__offers_draft_state')}
                    stateTone="weak"
                    amount={draft.total_amount}
                />
            ))}
            {quotes.map((quote) => (
                <QuoteOfferCard
                    key={quote.sales_order_id}
                    salesOrderId={quote.sales_order_id}
                    displayId={formatOrderId(appConfig, quote.display_id ?? quote.sales_order_id)}
                    customerId={customerId}
                    customerName={customerName}
                    amount={quote.total_amount_net ?? 0}
                    positionsCount={Number(quote.line_items_count ?? 0)}
                    margin={
                        appConfig.canReadGrossMargin && quote.total_margin !== null
                            ? quote.total_margin
                            : undefined
                    }
                    creator={quote.clerk_name ?? undefined}
                    kommission={quote.po_number ?? undefined}
                />
            ))}
        </LogInteractionOfferSection>
    );
}
